import React, { useEffect, useRef, useState } from "react";
import {
  MercadoPagoService,
  getMercadoPagoConfig,
} from "../../../shared/services";
import "./Styles.css";
import "./CardIcon.css";
import { useDeviceId } from "shared/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import { PayloadToken, useCardRepository } from "../services";
import { useSuperMutation } from "@vadiun/react-hooks";
import { useSnackbar } from "notistack";

const CustomCardForm = () => {
  const { userId } = useParams<{ userId: string }>();
  const { saveCard } = useCardRepository();
  const { enqueueSnackbar } = useSnackbar();

  // Configurar mutación sin mensajes automáticos
  const saveCardMutation = useSuperMutation(saveCard, {
    showSuccessMessage: false,
    showErrorMessage: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [cardIcon, setCardIcon] = useState<string | null>(null);
  const [cardIconLoading, setCardIconLoading] = useState(false);
  const [cardType, setCardType] = useState<string | null>(null);
  const cardNumberFieldVoidMessage = "número requerido";
  const expirationDateFieldVoidMessage = "fecha requerida";
  const securityCodeFieldVoidMessage = "CVV requerido";
  const cardholderNameFieldVoidMessage = "Nombre requerido";
  const documentTypeFieldVoidMessage = "Tipo de documento requerido";
  const documentNumberFieldVoidMessage = "Número requerido";

  // Función para obtener URL de imagen SVG de alta calidad basado en el ID
  const getHighQualityCardIconUrl = (
    paymentMethodId: string
  ): string | null => {
    const normalizedId = paymentMethodId.toLowerCase().replace(/[^a-z]/g, "");

    if (normalizedId.includes("visa")) {
      return "/visa-logo.svg";
    } else if (normalizedId.includes("master")) {
      return "/ma_symbol.svg";
    } else if (
      normalizedId.includes("amex") ||
      normalizedId.includes("american")
    ) {
      return "/American_Express-Logo.svg";
    }

    return null;
  };
  const [ErrorMessageCardNumber, setErrorMessageCardNumber] = useState<
    string | null
  >(cardNumberFieldVoidMessage);
  const [showErrors, setShowErrors] = useState<{
    show: boolean;
    firstInstance: boolean;
  }>({ show: false, firstInstance: true });

  const [ErrorMessageExpirationDate, setErrorMessageExpirationDate] = useState<
    string | null
  >(expirationDateFieldVoidMessage);

  const [ErrorMessageSecurityCode, setErrorMessageSecurityCode] = useState<
    string | null
  >(securityCodeFieldVoidMessage);

  const [isHoveringSecurityCode, setIsHoveringSecurityCode] = useState(false);
  const [isHoveringExpirationDate, setIsHoveringExpirationDate] =
    useState(false);
  const [isHoveringCardNumber, setIsHoveringCardNumber] = useState(false);
  const [isHoveringCardholderName, setIsHoveringCardholderName] =
    useState(false);

  const [cardholderName, setCardholderName] = useState("");
  const [ErrorMessageCardholderName, setErrorMessageCardholderName] = useState<
    string | null
  >(cardholderNameFieldVoidMessage);

  // Campos de identificación
  const [identificationType, setIdentificationType] = useState("DNI");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [identificationTypes, setIdentificationTypes] = useState<any[]>([]);
  const [ErrorMessageIdentificationType, setErrorMessageIdentificationType] =
    useState<string | null>(null);
  const [
    ErrorMessageIdentificationNumber,
    setErrorMessageIdentificationNumber,
  ] = useState<string | null>(documentNumberFieldVoidMessage);
  const [isHoveringIdentificationType, setIsHoveringIdentificationType] =
    useState(false);
  const [isHoveringIdentificationNumber, setIsHoveringIdentificationNumber] =
    useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const {
    deviceId,
    isLoading: isDeviceIdLoading,
    error: deviceIdError,
  } = useDeviceId();

  const mpInstance = useRef<any>(null);
  const fields = useRef({
    cardNumber: null,
    expirationDate: null,
    securityCode: null,
  });

  useEffect(() => {
    const initializeComponent = async () => {
      if (!userId) {
        return;
      }
      try {
        if (isDeviceIdLoading) {
          return;
        }
        if (!deviceId) {
          setSecurityError(
            "Error de seguridad: No se pudo obtener el Device ID. Por favor, recarga la página."
          );
          console.error(
            "❌ BLOQUEADO: No se puede cargar MercadoPago sin Device ID"
          );
          return;
        }
        if (deviceIdError) {
          setSecurityError(
            `Error de seguridad: ${deviceIdError}. Por favor, recarga la página.`
          );
          console.error("❌ BLOQUEADO: Error en Device ID:", deviceIdError);
          return;
        }
        const config = getMercadoPagoConfig(deviceId);
        // Validar que el userId esté presente

        console.log("✅ Usuario identificado:", userId);

        await MercadoPagoService.getInstance().initialize(config);
        initializeMercadoPago();
      } catch (error) {
        console.error("Error inicializando MercadoPago:", error);
        setMessage("Error al cargar el sistema de pagos");
      }
    };

    initializeComponent();

    return () => {
      unmountFields();
    };
  }, [deviceId, isDeviceIdLoading, deviceIdError, userId]);

  const initializeMercadoPago = async () => {
    try {
      mpInstance.current = MercadoPagoService.getInstance().getInstance();

      // Obtener tipos de identificación de MercadoPago
      await getIdentificationTypes();

      const checkDOM = setInterval(() => {
        const containers = [
          document.getElementById("mp-card-number"),
          document.getElementById("mp-expiration-date"),
          document.getElementById("mp-security-code"),
        ];

        if (containers.every((container) => container !== null)) {
          clearInterval(checkDOM);
          mountFields();
          setSdkReady(true);
          setFormReady(true);
        }
      }, 100);
    } catch (error) {
      console.error("Error inicializando MercadoPago:", error);
      setMessage("Error al inicializar el sistema de pagos");
    }
  };

  const getIdentificationTypes = async () => {
    try {
      const types = await mpInstance.current.getIdentificationTypes();
      setIdentificationTypes(types);
      console.log("Tipos de identificación obtenidos:", types);
    } catch (error) {
      console.error("Error obteniendo tipos de identificación:", error);
    }
  };

  const mountFields = () => {
    try {
      const cardNumberField = mpInstance.current.fields
        .create("cardNumber", { placeholder: "1234 5678 9012 3456" })
        .mount("mp-card-number");

      cardNumberField.on("binChange", async (data: any) => {
        const { bin } = data;
        if (bin && mpInstance.current) {
          setCardIconLoading(true);
          try {
            const { results } = await mpInstance.current.getPaymentMethods({
              bin,
            });
            const paymentMethod = results[0];
            if (paymentMethod && paymentMethod.id) {
              // Detectar tipo de tarjeta
              const cardTypeName = paymentMethod.id?.toLowerCase() || "";
              setCardType(cardTypeName);

              // Verificar si debe usar imagen SVG de alta calidad o thumbnail
              const highQualityIconUrl = getHighQualityCardIconUrl(
                paymentMethod.id
              );

              if (highQualityIconUrl) {
                // Usar imagen SVG de alta calidad para Visa, Mastercard, Amex
                const img = new Image();
                img.onload = () => {
                  setCardIcon(highQualityIconUrl);
                  setCardIconLoading(false);
                };
                img.onerror = () => {
                  // Fallback al thumbnail original si falla la imagen de alta calidad
                  if (paymentMethod.thumbnail) {
                    const fallbackImg = new Image();
                    fallbackImg.onload = () => {
                      setCardIcon(paymentMethod.thumbnail);
                      setCardIconLoading(false);
                    };
                    fallbackImg.onerror = () => {
                      setCardIcon(null);
                      setCardIconLoading(false);
                    };
                    fallbackImg.src = paymentMethod.thumbnail;
                  } else {
                    setCardIcon(null);
                    setCardIconLoading(false);
                  }
                };
                img.src = highQualityIconUrl;
              } else if (paymentMethod.thumbnail) {
                // Usar thumbnail original para otros tipos
                const img = new Image();
                img.onload = () => {
                  setCardIcon(paymentMethod.thumbnail);
                  setCardIconLoading(false);
                };
                img.onerror = () => {
                  setCardIcon(null);
                  setCardIconLoading(false);
                };
                img.src = paymentMethod.thumbnail;
              } else {
                setCardIcon(null);
                setCardType(null);
                setCardIconLoading(false);
              }
            } else {
              setCardIcon(null);
              setCardType(null);
              setCardIconLoading(false);
            }
          } catch (error) {
            console.error("Error cargando ícono de tarjeta:", error);
            setCardIcon(null);
            setCardIconLoading(false);
          }
        } else {
          setCardIcon(null);
          setCardIconLoading(false);
        }
      });

      cardNumberField.on("validityChange", (data: any) => {
        const { errorMessages } = data;
        if (errorMessages.length > 0) {
          const invalidValueError = errorMessages.find(
            (err: any) => err.cause === "invalid_value"
          );
          if (invalidValueError) {
            setErrorMessageCardNumber(cardNumberFieldVoidMessage);
          } else {
            const invalidLengthError = errorMessages.find(
              (err: any) => err.cause === "invalid_length"
            );
            if (invalidLengthError) {
              setErrorMessageCardNumber("Debe tener entre 8 y 19 dígitos");
            } else {
              setErrorMessageCardNumber("Número de tarjeta inválido");
            }
          }
          if (!showErrors.firstInstance) {
            setShowErrors({ ...showErrors, show: true });
          }
        } else {
          setErrorMessageCardNumber(null);
        }
      });

      cardNumberField.on("focus", () => {
        setIsHoveringCardNumber(true);
      });
      cardNumberField.on("blur", () => {
        setIsHoveringCardNumber(false);
      });
      fields.current.cardNumber = cardNumberField;

      const expirationDateField = mpInstance.current.fields
        .create("expirationDate", { placeholder: "MM/YY" })
        .mount("mp-expiration-date");

      expirationDateField.on("validityChange", (data: any) => {
        const { errorMessages } = data;
        if (errorMessages.length > 0) {
          const invalidTypeError = errorMessages.find(
            (err: any) =>
              err.cause === "invalid_type" && err.field === "expirationDate"
          );
          const invalidLengthError = errorMessages.find(
            (err: any) =>
              err.cause === "invalid_length" && err.field === "expirationDate"
          );
          if (invalidTypeError) {
            setErrorMessageExpirationDate(expirationDateFieldVoidMessage);
          } else if (invalidLengthError) {
            setErrorMessageExpirationDate("4 dígitos");
          } else {
            setErrorMessageExpirationDate("Expiración inválida");
          }
          if (!showErrors.firstInstance) {
            setShowErrors({ ...showErrors, show: true });
          }
        } else {
          setErrorMessageExpirationDate(null);
        }
      });

      expirationDateField.on("focus", () => {
        setIsHoveringExpirationDate(true);
      });
      expirationDateField.on("blur", () => {
        setIsHoveringExpirationDate(false);
      });

      fields.current.expirationDate = expirationDateField;

      const securityCodeField = mpInstance.current.fields
        .create("securityCode", { placeholder: "123" })
        .mount("mp-security-code");

      securityCodeField.on("focus", () => {
        setIsHoveringSecurityCode(true);
      });
      securityCodeField.on("blur", () => {
        setIsHoveringSecurityCode(false);
      });
      securityCodeField.on("validityChange", (data: any) => {
        const { errorMessages } = data;
        if (errorMessages.length > 0) {
          const invalidValueError = errorMessages.find(
            (err: any) => err.cause === "invalid_value"
          );

          if (invalidValueError) {
            setErrorMessageSecurityCode(securityCodeFieldVoidMessage);
          } else {
            const invalidLengthError = errorMessages.find(
              (err: any) => err.cause === "invalid_length"
            );

            if (invalidLengthError) {
              setErrorMessageSecurityCode("3 o 4 dígitos");
            } else {
              setErrorMessageSecurityCode("CVV inválido");
            }
          }
          if (!showErrors.firstInstance) {
            setShowErrors({ ...showErrors, show: true });
          }
        } else {
          setErrorMessageSecurityCode(null);
        }
      });
      fields.current.securityCode = securityCodeField;
    } catch (error) {
      console.error("Error montando campos:", error);
      setMessage("Error al crear campos de pago");
    }
  };

  const unmountFields = () => {
    Object.values(fields.current).forEach((field: any) => {
      if (field && typeof field.unmount === "function") {
        field.unmount();
      }
    });
  };

  const validateCardholderName = (name: string) => {
    if (!name.trim()) {
      setErrorMessageCardholderName(cardholderNameFieldVoidMessage);
      return false;
    }
    if (name.trim().length < 4) {
      setErrorMessageCardholderName(
        "El nombre debe tener al menos 4 caracteres"
      );
      return false;
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(name.trim())) {
      setErrorMessageCardholderName("El nombre solo puede contener letras");
      return false;
    }
    setErrorMessageCardholderName(null);
    return true;
  };

  const validateIdentificationType = (type: string) => {
    if (!type) {
      setErrorMessageIdentificationType(documentTypeFieldVoidMessage);
      return false;
    }
    setErrorMessageIdentificationType(null);
    return true;
  };

  const validateIdentificationNumber = (number: string) => {
    if (!number.trim()) {
      setErrorMessageIdentificationNumber(documentNumberFieldVoidMessage);
      return false;
    }
    if (!/^\d+$/.test(number.trim())) {
      setErrorMessageIdentificationNumber("Solo se permiten números");
      return false;
    }
    if (number.trim().length < 7) {
      setErrorMessageIdentificationNumber("Mínimo 7 dígitos");
      return false;
    }
    if (number.trim().length > 12) {
      setErrorMessageIdentificationNumber("Máximo 12 dígitos");
      return false;
    }
    setErrorMessageIdentificationNumber(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sdkReady || isLoading) return;

    let hasError = false;

    if (
      ErrorMessageCardNumber ||
      ErrorMessageExpirationDate ||
      ErrorMessageSecurityCode ||
      !validateCardholderName(cardholderName) ||
      !validateIdentificationType(identificationType) ||
      !validateIdentificationNumber(identificationNumber)
    ) {
      setShowErrors({ show: true, firstInstance: false });
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      // Preparar datos del usuario para el token
      const userData: any = {
        cardholderName: cardholderName.trim(),
        identificationType: identificationType,
        identificationNumber: identificationNumber.trim(),
      };
     
      const tokenTokenization = await mpInstance.current.fields.createCardToken(userData);

      const fullNameParts = tokenTokenization.cardholder.name.trim().split(" ");
      const lastName = fullNameParts[0];
      const firstName = fullNameParts.slice(1).join(" ") || fullNameParts[0];
      const cardholder = tokenTokenization.cardholder;
      const payloadToSaveCard: PayloadToken = {
        last_name: lastName,
        first_name: firstName,
        cardholder_name: tokenTokenization.cardholder.name,
        document_type: tokenTokenization.cardholder.identification.type,
        document_number: tokenTokenization.cardholder.identification.number,
        token: tokenTokenization.id,
        user_id: userId!,
        device_id: deviceId!,
      };

      const response = await saveCardMutation.mutateAsync(payloadToSaveCard);
      if (response.data?.is_valid) {
        const tokenToPay = await mpInstance.current.fields.createCardToken({
          cardId: response.data.card_id,
        });
        console.log("token1", tokenToPay);
        const tokeFinal = {
          ...tokenToPay,
          card_number_length: tokenTokenization.card_number_length,
          cardholder: cardholder,
          expiration_month: tokenTokenization.expiration_month,
          expiration_year: tokenTokenization.expiration_year,
          first_six_digits: tokenTokenization.first_six_digits,
          last_four_digits: tokenTokenization.last_four_digits,
          trunc_card_number: tokenTokenization.trunc_card_number,
        };
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ token: tokeFinal, deviceId: deviceId })
        );
      } else {
        // Tarjeta inválida - mostrar snackbar
        enqueueSnackbar(
          "La tarjeta es inválida. Por favor, intenta con otra tarjeta.",
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          }
        );
      }
    } catch (error: any) {
      console.error("Error en el proceso:", error);
      enqueueSnackbar(
        "La tarjeta es inválida. Por favor, intenta con otra tarjeta.",
        {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "center" },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (securityError) {
    return (
      <div
        className="mp-container-ios"
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="text-center">
            <div className="mb-4 text-6xl text-red-600">🔒</div>
            <h2 className="mb-2 text-xl font-bold text-red-800">
              Error de Seguridad
            </h2>
            <p className="mb-4 text-red-700">{securityError}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-red-600 px-6 py-2 text-white hover:bg-red-700"
            >
              Recargar Página
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mp-container-ios"
      style={{
        height: "100vh", // Usar viewport height completo
        maxHeight: "100vh", // Asegurar que no exceda la pantalla
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "500px",
        margin: "auto",
        backgroundColor: "transparent",
        paddingTop: "20px",
        paddingBottom: "20px",
        paddingLeft: "16px",
        paddingRight: "16px",
        boxSizing: "border-box", // Incluir padding en el cálculo de altura
      }}
    >
      {!formReady && <div className="loading"></div>}

      {/* Overlay de loading mientras se procesa */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            }}
          >
            {/* Animación de tarjeta */}
            <div
              style={{
                fontSize: "80px",
                animation: "pulse 1.5s ease-in-out infinite",
                color: "#37bdf8",
              }}
            >
              <FontAwesomeIcon icon={faCreditCard} />
            </div>
            <p
              style={{
                color: "white",
                fontSize: "18px",
                fontWeight: "500",
                textAlign: "center",
                margin: 0,
              }}
            >
              Procesando tarjeta...
            </p>
            <div className="spinner"></div>
          </div>
        </div>
      )}

      <div className={formReady ? "mp-content-ios" : "hidden"}>
        {message && (
          <div
            className={`mb-4 rounded-lg p-3 text-center ${
              message.includes("Error")
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mp-form-ios">
          <div className="mb-8 flex flex-col gap-8">
            <div className="mp-input-general">
              <label
                htmlFor="mp-card-number"
                className={`mp-label-general ${
                  ErrorMessageCardNumber && showErrors.show
                    ? "mp-text-error"
                    : isHoveringCardNumber
                    ? "mp-label-general-hover"
                    : "mp-text-normal"
                }`}
              >
                Número de tarjeta
              </label>
              <div
                id="mp-card-number"
                className={`mp-card-number ${
                  ErrorMessageCardNumber && showErrors.show
                    ? "error-border"
                    : isHoveringCardNumber
                    ? "hover-border"
                    : ""
                }`}
              ></div>
              {ErrorMessageCardNumber && showErrors.show && (
                <div className="mp-message-error">{ErrorMessageCardNumber}</div>
              )}
              {cardIconLoading && (
                <div
                  className="card-icon-container"
                  style={{
                    position: "absolute",
                    right: "9.5px",
                    bottom: "12px",
                  }}
                >
                  <div className="card-icon-loading"></div>
                </div>
              )}
              {cardIcon && !cardIconLoading && (
                <div
                  className="card-icon-container"
                  style={{
                    position: "absolute",
                    right: cardType === "visa" ?"14px": cardType === "amex" ? "2px" :"8.5px",
                    bottom: cardType === "visa" ? "16.5px" : "9.5px",
                  }}
                >
                  <img
                    src={cardIcon}
                    alt="Card icon"
                    className={`card-icon ${cardType ? cardType : ""}`}
                    onError={(e) => {
                      // Fallback si la imagen falla
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-8">
              <div className="mp-input-general">
                <label
                  htmlFor="mp-expiration-date"
                  className={`mp-label-general ${
                    ErrorMessageExpirationDate && showErrors.show
                      ? "mp-text-error"
                      : isHoveringExpirationDate
                      ? "mp-label-general-hover"
                      : "mp-text-normal"
                  }`}
                >
                  Expiración
                </label>
                <div
                  id="mp-expiration-date"
                  className={
                    ErrorMessageExpirationDate && showErrors.show
                      ? "error-border"
                      : isHoveringExpirationDate
                      ? "hover-border"
                      : ""
                  }
                ></div>
                {ErrorMessageExpirationDate && showErrors.show && (
                  <div className="mp-message-error">
                    {ErrorMessageExpirationDate}
                  </div>
                )}
              </div>
              <div className="mp-input-general">
                <label
                  htmlFor="mp-security-code"
                  className={`mp-label-general ${
                    ErrorMessageSecurityCode && showErrors.show
                      ? "mp-text-error"
                      : isHoveringSecurityCode
                      ? "mp-label-general-hover"
                      : "mp-text-normal"
                  }`}
                >
                  CVV
                </label>
                <div
                  id="mp-security-code"
                  className={
                    ErrorMessageSecurityCode && showErrors.show
                      ? "error-border"
                      : isHoveringSecurityCode
                      ? "hover-border"
                      : ""
                  }
                ></div>
                {ErrorMessageSecurityCode && showErrors.show && (
                  <div
                    className={`mp-message-error ${
                      ErrorMessageSecurityCode && showErrors.show
                        ? "mp-text-error"
                        : "mp-text-normal"
                    }`}
                  >
                    {ErrorMessageSecurityCode}
                  </div>
                )}
              </div>
            </div>
            <div className="mp-input-general">
              <label
                htmlFor="mp-cardholder-name"
                className={`mp-label-general-mixed ${
                  ErrorMessageCardholderName && showErrors.show
                    ? "mp-text-error"
                    : isHoveringCardholderName
                    ? "mp-label-general-hover"
                    : "mp-text-normal"
                }`}
              >
                <strong>Nombre</strong> (como aparece en la tarjeta)
              </label>
              <input
                id="mp-cardholder-name"
                type="text"
                placeholder="JUAN PEREZ"
                value={cardholderName}
                onFocus={() => {
                  setIsHoveringCardholderName(true);
                }}
                onBlur={() => {
                  setIsHoveringCardholderName(false);
                }}
                onChange={(e) => {
                  const upperCaseValue = e.target.value.toUpperCase();
                  setCardholderName(upperCaseValue);
                  validateCardholderName(upperCaseValue);
                }}
                className={`mp-cardholder-name ${
                  ErrorMessageCardholderName && showErrors.show
                    ? "error-border"
                    : isHoveringCardholderName
                    ? "hover-border"
                    : ""
                }`}
              />
              {ErrorMessageCardholderName && showErrors.show && (
                <div className="mp-message-error">
                  {ErrorMessageCardholderName}
                </div>
              )}
            </div>
            <div className="flex gap-8">
              <div
                className="mp-input-general"
                style={{ position: "relative" }}
              >
                <label
                  htmlFor="mp-identification-type"
                  className={`mp-label-general ${
                    ErrorMessageIdentificationType && showErrors.show
                      ? "mp-text-error"
                      : isHoveringIdentificationType
                      ? "mp-label-general-hover"
                      : "mp-text-normal"
                  }`}
                >
                  Tipo de ident.
                </label>
                <select
                  id="mp-identification-type"
                  value={identificationType}
                  onFocus={() => {
                    setIsHoveringIdentificationType(true);
                  }}
                  onBlur={() => {
                    setIsHoveringIdentificationType(false);
                    setIsSelectOpen(false);
                  }}
                  onMouseDown={() => {
                    setIsSelectOpen(!isSelectOpen);
                  }}
                  onChange={(e) => {
                    setIdentificationType(e.target.value);
                    setIsSelectOpen(false); // Cierra el select y rota el icono
                    validateIdentificationType(e.target.value);
                  }}
                  className={`mp-cardholder-name ${
                    ErrorMessageIdentificationType && showErrors.show
                      ? "error-border"
                      : isHoveringIdentificationType
                      ? "hover-border"
                      : ""
                  }`}
                >
                  <option value="" disabled>
                    Seleccionar
                  </option>
                  {identificationTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  id="mp-identification-type-icon"
                  style={{
                    transform: isSelectOpen
                      ? "translateY(-50%) rotate(180deg)"
                      : "translateY(-50%) rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
                {ErrorMessageIdentificationType && showErrors.show && (
                  <div className="mp-message-error">
                    {ErrorMessageIdentificationType}
                  </div>
                )}
              </div>
              <div className="mp-input-general">
                <label
                  htmlFor="mp-identification-number"
                  className={`mp-label-general ${
                    ErrorMessageIdentificationNumber && showErrors.show
                      ? "mp-text-error"
                      : isHoveringIdentificationNumber
                      ? "mp-label-general-hover"
                      : "mp-text-normal"
                  }`}
                >
                  Núm. de ident.
                </label>
                <input
                  id="mp-identification-number"
                  type="text"
                  placeholder="12345678"
                  value={identificationNumber}
                  onFocus={() => {
                    setIsHoveringIdentificationNumber(true);
                  }}
                  onBlur={() => {
                    setIsHoveringIdentificationNumber(false);
                  }}
                  onChange={(e) => {
                    setIdentificationNumber(e.target.value);
                    validateIdentificationNumber(e.target.value);
                  }}
                  className={`mp-cardholder-name ${
                    ErrorMessageIdentificationNumber && showErrors.show
                      ? "error-border"
                      : isHoveringIdentificationNumber
                      ? "hover-border"
                      : ""
                  }`}
                />
                {ErrorMessageIdentificationNumber && showErrors.show && (
                  <div className="mp-message-error">
                    {ErrorMessageIdentificationNumber}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mp-buttons-container">
            <button
              type="submit"
              disabled={!sdkReady || isLoading}
              className="mp-button"
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Procesando...
                </>
              ) : (
                "Agregar tarjeta"
              )}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => {
                if (window.ReactNativeWebView) {
                  window.ReactNativeWebView?.postMessage(
                    JSON.stringify({ action: "cancel" })
                  );
                }
              }}
              className="mp-button-cancel"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomCardForm;

declare global {
  interface Window {
    MercadoPago: any;
    ReactNativeWebView: {
      postMessage: (message: string) => void;
    };
  }
}
